import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiServices from "../../../../services/apiService";

const ParkingSlotForm = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    // Dropdown Data
    const [sites, setSites] = useState([]);
    const [wings, setWings] = useState([]);
    const [floors, setFloors] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [parkingTypes, setParkingTypes] = useState([]);

    const [formData, setFormData] = useState({
        slotName: '',
        siteId: '',
        wingId: '',
        floorId: '',
        parkingTypeId: '',
        vehicleTypeId: ''
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Fetch Sites
                const siteRes = await apiServices({ url: '/SiteDetailsMaster/GetAllSiteDetails', method: 'GET' });
                if (siteRes.success) setSites(siteRes.data?.data || []);

                // Fetch Vehicle Types
                const vehicleRes = await apiServices({ url: '/VehicleTypeMaster/getAllVehicleType', method: 'GET' });
                if (vehicleRes.success) setVehicleTypes(vehicleRes.data?.data || []);

                // Fetch Parking Types
                const parkingRes = await apiServices({ url: '/ParkingTypeMaster/GetAllParkingTypes', method: 'GET' });
                if (parkingRes.success) setParkingTypes(parkingRes.data?.data || []);

            } catch (error) {
                console.error("Error loading dropdown data:", error);
                toast.error("Failed to load form data.");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    // Cascade dropdowns
    const fetchWings = async (siteId) => {
        try {
            const res = await apiServices({ url: '/WingMaster/GetAllWing', method: 'GET' });
            if (res.success && res.data?.WingMasterData) {
                setWings(res.data.WingMasterData.filter(w => w.SiteId === Number(siteId)));
            }
        } catch (error) {
            console.error("Error fetching wings", error);
        }
    };

    const fetchFloors = async (wingId) => {
        try {
            const res = await apiServices({ url: '/FloorMaster/GetAllFloor', method: 'GET' });
            if (res.success && res.data?.FloorMasterData) {
                setFloors(res.data.FloorMasterData.filter(f => f.WingId === Number(wingId)));
            }
        } catch (error) {
            console.error("Error fetching floors", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'siteId') {
            setFormData(prev => ({ ...prev, wingId: '', floorId: '' }));
            setFloors([]);
            if (value) fetchWings(value);
            else setWings([]);
        }

        if (name === 'wingId') {
            setFormData(prev => ({ ...prev, floorId: '' }));
            if (value) fetchFloors(value);
            else setFloors([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.slotName || !formData.siteId || !formData.wingId || !formData.floorId || !formData.parkingTypeId || !formData.vehicleTypeId) {
            toast.error("Please fill all mandatory fields.");
            return;
        }

        setSubmitting(true);
        
        const body = {
            slotName: formData.slotName,
            siteId: Number(formData.siteId),
            wingId: Number(formData.wingId),
            floorId: Number(formData.floorId),
            parkingTypeId: Number(formData.parkingTypeId),
            vehicleTypeId: Number(formData.vehicleTypeId),
            createdBy: "sys"
        };

        try {
            const res = await apiServices({
                url: `/ParkingSlotMaster/postParkingSlotMaster`,
                method: 'POST',
                data: body
            });

            if (res.success && res.data?.isSuccess) {
                toast.success('Parking slot added successfully!');
                navigate("/zentres/parking-management/parking-slot");
            } else {
                toast.error(res.data?.message || "Failed to add parking slot.");
            }
        } catch (error) {
            console.error("Error saving parking slot:", error);
            toast.error("An error occurred while saving.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-6">Loading...</div>;

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Add Parking Slot</h1>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Site */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Site *</label>
                            <select
                                name="siteId"
                                value={formData.siteId}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-red-500"
                            >
                                <option value="">Select Site</option>
                                {sites.map(s => <option key={s.SiteId} value={s.SiteId}>{s.SiteName}</option>)}
                            </select>
                        </div>

                        {/* Wing */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Wing *</label>
                            <select
                                name="wingId"
                                value={formData.wingId}
                                onChange={handleChange}
                                required
                                disabled={!formData.siteId}
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-red-500 disabled:bg-gray-100"
                            >
                                <option value="">Select Wing</option>
                                {wings.map(w => <option key={w.WingId} value={w.WingId}>{w.WingName}</option>)}
                            </select>
                        </div>

                        {/* Floor */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Floor *</label>
                            <select
                                name="floorId"
                                value={formData.floorId}
                                onChange={handleChange}
                                required
                                disabled={!formData.wingId}
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-red-500 disabled:bg-gray-100"
                            >
                                <option value="">Select Floor</option>
                                {floors.map(f => <option key={f.FloorId} value={f.FloorId}>{f.FloorName}</option>)}
                            </select>
                        </div>

                        {/* Slot Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Slot Name *</label>
                            <input
                                type="text"
                                name="slotName"
                                value={formData.slotName}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-red-500"
                            />
                        </div>

                        {/* Vehicle Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type *</label>
                            <select
                                name="vehicleTypeId"
                                value={formData.vehicleTypeId}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-red-500"
                            >
                                <option value="">Select Vehicle Type</option>
                                {vehicleTypes.map(v => <option key={v.Id} value={v.Id}>{v.VehicleType}</option>)}
                            </select>
                        </div>

                        {/* Parking Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Parking Type *</label>
                            <select
                                name="parkingTypeId"
                                value={formData.parkingTypeId}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-red-500"
                            >
                                <option value="">Select Parking Type</option>
                                {parkingTypes.map(p => <option key={p.Id} value={p.Id}>{p.ParkingType}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="flex gap-4 pt-4 border-t">
                        <button
                            type="button"
                            onClick={() => navigate("/zentres/parking-management/parking-slot")}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2 bg-[#E31F26] text-white rounded-lg hover:bg-[#c11a20] transition-colors disabled:opacity-50"
                        >
                            {submitting ? 'Saving...' : 'Add Slot'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ParkingSlotForm;
